
import React, { useEffect, useState } from "react";
import useDeferredRender from "@/hooks/use-deferred-render";
import { useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { toast } from "sonner";
import logger from "@/utils/logger";
import SubscriptionRequired from "@/components/subscription/SubscriptionRequired";

interface RequireSubscriptionProps {
  children: React.ReactNode;
  message?: string;
}

/**
 * Component that ensures users have an active subscription
 * If not, displays the SubscriptionRequired component
 */
const RequireSubscription: React.FC<RequireSubscriptionProps> = ({
  children,
  message,
}) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const subscription = searchParams.get("subscription");
  const sessionRefresh = searchParams.get("session_refresh");
  const { user, isLoading: isAuthLoading, lastAuthEvent, lastAuthEventTime } = useAuth();
  // Use the cached subscription status to reduce loading screens
  // Force skipCache to false during login transitions to prevent flicker
  const loginTransitionActive = lastAuthEvent === 'SIGNED_IN' && 
    lastAuthEventTime && 
    (Date.now() - lastAuthEventTime < 5000); // Extended grace period to 5 seconds
    
  const {
    hasActiveSubscription,
    isLoading: isSubscriptionLoading,
    error,
    refetch: refetchSubscription,
  } = useSubscriptionStatus({ 
    skipCache: false,
    // Treat as loading during login transition
    forceLoadingState: loginTransitionActive 
  });
  const [isVerifying, setIsVerifying] = useState(true);
  const [hasRefetched, setHasRefetched] = useState(false);

  // Store login transition state in sessionStorage to persist across route changes
  useEffect(() => {
    if (loginTransitionActive) {
      try {
        sessionStorage.setItem('login_transition_active', 'true');
        sessionStorage.setItem('login_transition_timestamp', lastAuthEventTime?.toString() || '');
      } catch (e) {
        // Ignore sessionStorage errors
      }
    }
  }, [loginTransitionActive, lastAuthEventTime]);
  
  // Effect to handle subscription verification
  useEffect(() => {
    if (!isAuthLoading && !isSubscriptionLoading) {
      // If we're redirected after a successful subscription payment but haven't refetched yet,
      // try to refetch the subscription status before deciding the user doesn't have a subscription
      if (
        subscription === "success" &&
        sessionRefresh === "true" &&
        !hasRefetched
      ) {
        const fetchUpdatedSubscription = async () => {
          logger.info(
            "Refetching subscription status after successful payment"
          );
          await refetchSubscription();
          setHasRefetched(true);
          setIsVerifying(false);
        };

        fetchUpdatedSubscription();
      } else {
        setIsVerifying(false);
      }

      // If there was an error checking subscription status
      if (error) {
        logger.error("Error verifying subscription:", error);
        toast.error(
          "Could not verify your subscription status. Please try again later."
        );
      }
    }
  }, [
    isAuthLoading,
    isSubscriptionLoading,
    error,
    subscription,
    sessionRefresh,
    hasRefetched,
    refetchSubscription,
  ]);

  // Store the last known subscription status in sessionStorage to prevent flashing on refresh
  useEffect(() => {
    if (hasActiveSubscription !== null) {
      try {
        sessionStorage.setItem('mypetid_subscription_status', JSON.stringify({
          hasActiveSubscription,
          timestamp: Date.now()
        }));
      } catch (e) {
        // Ignore sessionStorage errors
      }
    }
  }, [hasActiveSubscription]);
  
  // Get cached subscription status from sessionStorage
  const getCachedSubscriptionStatus = (): boolean | null => {
    try {
      const cachedStatus = sessionStorage.getItem('mypetid_subscription_status');
      if (cachedStatus) {
        const { hasActiveSubscription: cachedHasActive, timestamp } = JSON.parse(cachedStatus);
        // Only use cache if it's less than 10 minutes old
        if (Date.now() - timestamp < 10 * 60 * 1000) {
          logger.info("Found valid cached subscription status in session storage");
          return cachedHasActive;
        }
      }
    } catch (e) {
      // Ignore sessionStorage errors
    }
    return null;
  };
  
  // Use cached status if available
  const cachedStatus = getCachedSubscriptionStatus();
  
  // Use deferred rendering to prevent loader flashing when navigating between tabs
  const shouldRender = useDeferredRender(
    'subscription-check', // Component ID
    100, // Initial delay (ms) - reduced from 300ms to 100ms
    0,   // Subsequent delay (ms) - render immediately after first time
    [isVerifying, isAuthLoading, isSubscriptionLoading, hasActiveSubscription]
  );
  
  // Show loading state - but only if we don't have a cached result and need to render the loader
  // Skip the loader completely if we're just refreshing the page
  const isPageRefresh = window.performance && 
    window.performance.navigation && 
    window.performance.navigation.type === 1;
  
  if ((isVerifying || isAuthLoading || isSubscriptionLoading) && 
      hasActiveSubscription === null && 
      !shouldRender && 
      !isPageRefresh) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying subscription...</p>
        </div>
      </div>
    );
  }

  // Check if we're in a login transition from sessionStorage
  // This helps maintain the state across route changes
  const getLoginTransitionFromStorage = (): boolean => {
    try {
      const isActive = sessionStorage.getItem('login_transition_active') === 'true';
      if (isActive) {
        const timestamp = parseInt(sessionStorage.getItem('login_transition_timestamp') || '0', 10);
        // Only consider valid if within the last 5 seconds
        if (Date.now() - timestamp < 5000) {
          return true;
        } else {
          // Clear expired transition state
          sessionStorage.removeItem('login_transition_active');
          sessionStorage.removeItem('login_transition_timestamp');
        }
      }
    } catch (e) {
      // Ignore sessionStorage errors
    }
    return false;
  };
  
  // Determine if we should show the subscription required page
  const shouldShowSubscriptionRequired = () => {
    // If we have a definitive answer that the user has a subscription, don't show it
    if (hasActiveSubscription === true) {
      return false;
    }
    
    // If we have a cached status that says the user has a subscription, don't show it
    if (hasActiveSubscription === null && cachedStatus === true) {
      return false;
    }
    
    // Check if user just logged in (within last 5 seconds) - either from context or sessionStorage
    const justLoggedIn = loginTransitionActive || getLoginTransitionFromStorage();
      
    // If user just logged in, show loading instead of subscription required
    if (justLoggedIn) {
      logger.info('In login transition, showing loading state instead of subscription required');
      return false;
    }
    
    // Check if this is the initial page load or refresh
    // @ts-ignore - Accessing custom property
    const isInitialLoad = typeof window !== 'undefined' && window.mypetid_initial_load === true;
    
    // Check if this is a page refresh
    const isPageRefresh = window.performance && 
      ((window.performance.navigation && window.performance.navigation.type === 1) ||
      (window.performance.getEntriesByType && 
       window.performance.getEntriesByType('navigation')[0] && 
       (window.performance.getEntriesByType('navigation')[0] as any).type === 'reload'));
    
    // Detect if this is a mobile device
    const isMobileDevice = typeof window !== 'undefined' && 
      /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    // Special handling for mobile devices on refresh
    if (isMobileDevice && isPageRefresh) {
      logger.debug('Mobile refresh detected - avoiding subscription check redirect');
      // On mobile refresh, we always show the content and avoid redirects
      // This prevents the jarring redirect to landing page
      return false;
    }
    
    // Only skip showing subscription required during initial load if:
    // 1. We're on a page refresh AND
    // 2. We have previously confirmed the user has a subscription (via sessionStorage)
    if ((isSubscriptionLoading || isAuthLoading || isVerifying) && 
        isPageRefresh && 
        cachedStatus === true) {
      return false;
    }
    
    // If we're still loading but we've been on this page for less than 500ms, don't show it
    // This prevents the flash during initial load, but only for a very brief period
    if ((isSubscriptionLoading || hasActiveSubscription === null) && 
        (performance.now() < 500)) {
      return false;
    }
    
    // If we have a definitive answer that the user doesn't have a subscription, show it
    if (hasActiveSubscription === false) {
      return true;
    }
    
    // If we're not loading anymore and don't have a subscription status, show it
    // This handles the case where the subscription check failed
    if (!isSubscriptionLoading && !isAuthLoading && !isVerifying && hasActiveSubscription === null) {
      return true;
    }
    
    // Default to showing the subscription required page during uncertain states
    // This ensures users without a subscription don't get access to protected content
    // Only exception is if we have a cached status that says the user has a subscription
    return true;
  };
  
  // If user doesn't have an active subscription and we should show the subscription required page
  if (shouldShowSubscriptionRequired()) {
    logger.info(
      "User does not have an active subscription, showing subscription required message"
    );
    return (
      <SubscriptionRequired message={message} returnPath={location.pathname} />
    );
  }

  // Check if user just logged in (within last 5 seconds) - either from context or sessionStorage
  const justLoggedIn = loginTransitionActive || getLoginTransitionFromStorage();
  
  // Only render children if we're certain the user has a subscription
  // This prevents any glimpses of protected content during loading
  if (hasActiveSubscription === true || cachedStatus === true) {
    // If we're showing content, clear any login transition state
    if (justLoggedIn) {
      try {
        sessionStorage.removeItem('login_transition_active');
        sessionStorage.removeItem('login_transition_timestamp');
      } catch (e) {
        // Ignore sessionStorage errors
      }
    }
    return <>{children}</>;
  }
  
  // Show loading state while checking subscription status or if user just logged in
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-gray-600">{justLoggedIn ? "Redirecting to dashboard..." : "Verifying subscription..."}</p>
      </div>
    </div>
  );
};

export default RequireSubscription;
